import { Users } from 'lucide-react';

import { StudentProfileView } from '@/components/profile/StudentProfileView';
import { EmptyState } from '@/components/ui/EmptyState';
import { SecureImage } from '@/components/ui/SecureImage';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import { parentApi } from '../api/parent.api';
import { useParentChild, useSelectedChild } from '../store/child.store';
import { PARENT_PALETTE } from '../theme';

/**
 * Profile, for the child on screen — their record as the school office holds
 * it, drawn with the student panel's own `StudentProfileView`.
 *
 * Two reads, both already the parent panel's: `/profile` for the record (the
 * same server-side read as the child's own profile tab, with every other
 * guardian's number masked) and `/results` for the academic tab's marks — the
 * same call the Results screen makes, so the two cannot disagree.
 *
 * Read-only. A correction to an admission record — a date of birth, an
 * address, a phone number — is the school office's to make, as it is for the
 * child.
 *
 * The child is never in the address. It comes from the switcher in the sidebar,
 * and the server resolves which children the caller may see from their own
 * token — an id in the URL is an id somebody can change (CLAUDE.md §10).
 */
export function ParentProfilePage() {
  const child = useSelectedChild();
  const isLoadingChildren = useParentChild((state) => state.isLoading);
  const childrenError = useParentChild((state) => state.error);
  const reloadChildren = useParentChild((state) => state.load);

  const {
    data: profileData,
    isLoading: isLoadingProfile,
    error: profileError,
    refetch: refetchProfile,
  } = useApiQuery(
    () => (child ? parentApi.profile(child.uuid) : Promise.resolve(null)),
    [child?.uuid],
  );

  const {
    data: marksData,
    isLoading: isLoadingMarks,
    error: marksError,
    refetch: refetchMarks,
  } = useApiQuery(
    () => (child ? parentApi.results(child.uuid) : Promise.resolve(null)),
    [child?.uuid],
  );

  // Switching children refetches both; until the new ones land, the old ones
  // are another child's record and must not be drawn under this name.
  const profile = profileData && profileData.child.uuid === child?.uuid ? profileData : null;
  const marks = marksData && marksData.child.uuid === child?.uuid ? marksData : null;
  const error = profileError ?? marksError;

  if (isLoadingChildren) {
    return (
      <div className={cn(PARENT_PALETTE, 'flex flex-col gap-6')}>
        <Skeleton className="h-40 rounded-3xl" />
        <Skeleton className="h-96 rounded-3xl" />
      </div>
    );
  }

  if (childrenError) {
    return (
      <div className={cn(PARENT_PALETTE)}>
        <QueryError
          title="Could not load your children"
          error={new ApiRequestError(childrenError, 0, 'INTERNAL_ERROR')}
          onRetry={() => void reloadChildren()}
        />
      </div>
    );
  }

  if (!child) {
    return (
      <div className={cn(PARENT_PALETTE, 'card')}>
        <EmptyState
          icon={<Users className="h-5 w-5" />}
          title="No child linked to your account yet"
          description="Your school office links a parent's account to their child's record. Once that is done, your child's details appear here."
        />
      </div>
    );
  }

  return (
    <div className={cn(PARENT_PALETTE)}>
      <StudentProfileView
        // Remounted per child, so the open tab does not carry over.
        key={child.uuid}
        profile={profile?.header ?? null}
        record={profile?.record ?? null}
        marks={marks}
        isLoading={isLoadingProfile || isLoadingMarks || (!error && (!profile || !marks))}
        error={error}
        onRetry={() => {
          refetchProfile();
          refetchMarks();
        }}
        childName={child.fullName}
        photo={
          child.hasPhoto ? (
            <SecureImage
              path={parentApi.childPhotoPath(child.uuid)}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : undefined
        }
      />
    </div>
  );
}
